// Image-typed question detection — spec §1 of the Image Questions contract.
//
// A question's type hint is the value in the form's `questions` JSON, e.g.
// {"Upload a photo": "(Image)"}. Parse leniently on read: strip parens,
// lowercase, and treat as an image question if it contains "image" or
// "photo" — this mirrors the server's `_is_image_type()` in voice_servicer.py
// exactly, so typed-chat and voice agree on what counts as a photo field.

import FirestoreRepository from '../services/FirestoreRepository';
import { isChoiceTypeHint } from './choice';

/** The canonical write form used by the builder when adding an image question. */
export const IMAGE_TYPE_HINT = '(Image)';

/** Lenient check matching the server's `_is_image_type()`.
 *
 * Choice questions are checked FIRST and always return false here — an
 * option like "Photo shoot" must not turn a Choice question into a camera
 * question (Choice Questions contract §1, "CRITICAL — image detection"). */
export function isImageTypeHint(typeHint: string | undefined | null): boolean {
  if (isChoiceTypeHint(typeHint)) return false;
  const t = String(typeHint || '')
    .trim()
    .replace(/^\(+|\)+$/g, '')
    .toLowerCase();
  return t.includes('image') || t.includes('photo');
}

/** Flatten a form's `questions` JSON (Required/Optional, map-of-maps, or flat)
    into a single question-name -> type-hint map. */
export function flattenQuestionTypes(questionsJson: string | undefined | null): Record<string, string> {
  if (!questionsJson) return {};
  const repository = new FirestoreRepository();
  const parsed = repository.parseQuestions(questionsJson);
  const flat: Record<string, string> = {};
  Object.keys(parsed).forEach((section) => {
    const sectionQuestions = parsed[section];
    if (sectionQuestions && typeof sectionQuestions === 'object') {
      Object.keys(sectionQuestions).forEach((question) => {
        flat[question] = sectionQuestions[question];
      });
    }
  });
  return flat;
}

/** All question names in a form whose type hint is image/photo. */
export function getImageQuestionNames(questionsJson: string | undefined | null): string[] {
  const flat = flattenQuestionTypes(questionsJson);
  return Object.keys(flat).filter((question) => isImageTypeHint(flat[question]));
}

export function hasImageQuestions(questionsJson: string | undefined | null): boolean {
  return getImageQuestionNames(questionsJson).length > 0;
}

/** Storage download-URL prefix — an answer only counts as "has a photo" if it
    starts with this (spec §2). */
export const STORAGE_URL_PREFIX = 'https://firebasestorage.googleapis.com/';

export function isStagedPhotoUrl(value: string | undefined | null): boolean {
  return typeof value === 'string' && value.startsWith(STORAGE_URL_PREFIX);
}
